/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable @typescript-eslint/default-param-last */
import Grid from '@elliemae/ds-grid';
import { LockLockedRateRequested as LockIcon } from '@elliemae/ds-icons';
import { RequestLockStatus } from '../../../../common/constants/enumTypes';
import Rights from '../../../../common/services/Rights';
import { CustomHeader } from '../../../components/CustomColumnHeader/index';
import {
  checkLockRateStatus,
  dispApr,
  dispCurrencyMask,
  dispPrice,
} from '../../helpers';
import { StyledBestExProgramLinks, StyledBestExRowSelected } from './styled';

interface GetBestExColumnsProps {
  relockRequest?: any;
  selectedRowID?: string;
  handleSelectProduct?: any;
  handleRefreshButton?: any;
  handleSelectRow?: any;
  disableLockLinks?: boolean;
  windowWidth?: number;
  isBuyside?: boolean;
  isBuySideHistorical?: boolean;
  requestLockStatus?: number;
  columnWidth?: string;
}

const rightAlign = {
  width: '100%',
  textAlign: 'right',
  paddingRight: '3px',
  border: '1px none white',
};

const CellValue = ({ value, isBest, styles }) => {
  if (isBest) {
    return <span style={{ fontWeight: 'bold', ...styles }}>{value}</span>;
  }
  return <span style={{ ...styles }}>{value}</span>;
};

const getSelectedLock = (lockRequested, locksAvailable) => {
  const locksArr = locksAvailable.split(',').map(Number);
  const locks = locksArr.filter((lock) => lock >= lockRequested);
  return locks[0];
};

export const getBestExColumns = ({
  relockRequest = {},
  selectedRowID = '',
  handleSelectProduct,
  handleRefreshButton,
  handleSelectRow,
  disableLockLinks,
  windowWidth,
  isBuyside,
  isBuySideHistorical,
  requestLockStatus,
  columnWidth = Rights.viewAPR
    ? `${(48 / 6).toFixed(2)}%`
    : `${(48 / 5).toFixed(2)}%`,
}: GetBestExColumnsProps) => [
  {
    accessor: 'program',
    Header: 'Program Name',
    minWidth: '60px',
    width: '52%',
    Cell: ({ row }) => {
      const rowData = row.original;
      const isSelectedRow = rowData.id === selectedRowID;
      const { productTypeID } = rowData;
      const selectedLock = getSelectedLock(
        rowData.lockRequested,
        rowData.locksAvailable,
      );
      let productIcon;
      let altText;
      if (productTypeID === 2) {
        // TODO(ngen): moving all these files to just URL references from webroot
        productIcon = '/img/fha.gif';
        altText = 'FHA';
      } else if (productTypeID === 3) {
        productIcon = '/img/va.gif';
        altText = 'VA';
      } else if (productTypeID === 4) {
        productIcon = '/img/usda.gif';
        altText = 'USDA';
      }
      return (
        <StyledBestExRowSelected data-isSelected={isSelectedRow}>
          <CellValue
            isBest={rowData.isBest}
            styles={rightAlign}
            value={
              <Grid
                cols={[1, 1, 1]}
                style={{ alignItems: 'left', border: '1px none red' }}
              >
                <Grid style={{ width: '250px' }}>
                  <span
                    style={{
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      border: '1px none yellow',
                    }}
                  >
                    {rowData.productGroup}
                  </span>
                </Grid>
                <Grid
                  style={{
                    width: '70%',
                    paddingRight: '20px',
                    border: '1px none blue',
                    alignItems: 'center',
                    display: 'block',
                  }}
                >
                  <span>
                    ({' '}
                    {rowData.locksAvailable
                      .split(',')
                      .map((lock) =>
                        selectedLock !== Number(lock) ? (
                          <a
                            key={lock}
                            style={{
                              textDecoration: 'none',
                              pointerEvents: disableLockLinks
                                ? 'none'
                                : undefined,
                              color: disableLockLinks ? 'black' : undefined,
                            }}
                            onClick={() => handleRefreshButton(lock)}
                          >
                            {lock}
                          </a>
                        ) : (
                          <span style={{ fontWeight: 'bold' }} key={lock}>
                            {lock}
                          </span>
                        ),
                      )
                      .reduce((prev, curr) => [prev, ' ', curr])}{' '}
                    )
                  </span>
                </Grid>
                <Grid>
                  {productIcon && (
                    <img
                      src={productIcon}
                      alt={altText}
                      style={{ height: '10px' }}
                    />
                  )}
                </Grid>
              </Grid>
            }
          />
        </StyledBestExRowSelected>
      );
    },
  },
  {
    accessor: 'rate',
    Header: () => <CustomHeader label="Rate" />,
    width: columnWidth,
    sortable: true,
    resizable: true,
    Cell: ({ row }) => {
      const rowData = row.original.target;
      const { programID, rateDataID } = rowData;
      const lockRateStatus = checkLockRateStatus(
        relockRequest,
        programID,
        rateDataID,
      );

      const lockEffectiveDate =
        row.original.rateDetails.find(
          (rateDetail) => rateDetail.rateDataID === rateDataID,
        )?.lockEffectiveDate || null;
      const wcpPolicyApplied = row.original.wcpPolicyApplied;
      const displayLockIcon = isBuyside
        ? isBuySideHistorical && lockRateStatus
        : lockRateStatus && requestLockStatus !== RequestLockStatus.Cancelled;
      const isSelectedRow = rowData.id === selectedRowID;
      return (
        <StyledBestExRowSelected data-isSelected={isSelectedRow}>
          <Grid width="100%" justifyContent="flex-end" alignItems="center">
            {displayLockIcon && (
              <LockIcon
                color={['warning', '600']}
                title="Rate lock has been requested for this item"
                style={{ marginRight: '8px' }}
              />
            )}
            <StyledBestExProgramLinks
              onClick={() => {
                handleSelectRow(rowData);
                handleSelectProduct(
                  programID,
                  rateDataID,
                  lockEffectiveDate,
                  wcpPolicyApplied,
                );
              }}
            >
              {rowData.rate ? `${rowData.rate.toFixed(3)}` : null}
            </StyledBestExProgramLinks>
          </Grid>
        </StyledBestExRowSelected>
      );
    },
  },
  {
    accessor: 'price',
    Header: () => <CustomHeader label="PRICE" />,
    width: columnWidth,
    searchable: true,
    resizable: true,
    Cell: ({ row }) => {
      const rowData = row.original.target;
      const isSelectedRow = rowData.id === selectedRowID;
      return (
        <StyledBestExRowSelected data-isSelected={isSelectedRow}>
          <CellValue
            styles={rightAlign}
            value={dispPrice(rowData.price, rowData.priceVal)}
            isBest={rowData.isBest}
          />
        </StyledBestExRowSelected>
      );
    },
  },
  {
    accessor: 'discount',
    Header: () => <CustomHeader label="Price ($)" />,
    width: columnWidth,
    Cell: ({ row }) => {
      const rowData = row.original.target;
      const isSelectedRow = rowData.id === selectedRowID;
      if (!rowData.parent) {
        return (
          <StyledBestExRowSelected data-isSelected={isSelectedRow}>
            <CellValue
              styles={rightAlign}
              value={dispCurrencyMask(rowData.priceVal)}
              isBest={rowData.isBest}
            />
          </StyledBestExRowSelected>
        );
      }
      return '';
    },
  },
  {
    accessor: 'pnI',
    Header: () => <CustomHeader label="PMT" />,
    width: columnWidth,
    resizable: true,
    Cell: ({ row }) => {
      const rowData = row.original.target;
      const isSelectedRow = rowData.id === selectedRowID;
      return (
        <StyledBestExRowSelected data-isSelected={isSelectedRow}>
          <CellValue
            styles={rightAlign}
            value={`$${parseInt(rowData.pnI, 10)}`}
            isBest={rowData.isBest}
          />
        </StyledBestExRowSelected>
      );
    },
  },
  ...(Rights.viewAPR
    ? [
        {
          accessor: 'apr',
          Header: () => <CustomHeader label="APR" />,
          width: columnWidth,
          Cell: ({ row }) => {
            const rowData = row.original.target;
            const isSelectedRow = rowData.id === selectedRowID;
            if (!rowData.parent) {
              return (
                <StyledBestExRowSelected data-isSelected={isSelectedRow}>
                  <CellValue
                    styles={rightAlign}
                    value={dispApr(rowData.apr)}
                    isBest={rowData.isBest}
                  />
                </StyledBestExRowSelected>
              );
            }
            return '';
          },
        },
      ]
    : []),
  {
    accessor: 'details',
    Header: () => <CustomHeader label="QM/DET" />,
    width: columnWidth,
    searchable: true,
    resizable: true,
    Cell: ({ row, onClickPrice }) => {
      const rowData = row.original.target;
      const isSelectedRow = rowData.id === selectedRowID;
      const { programID, rateDataID } = rowData;
      const lockEffectiveDate =
        row.original.rateDetails.find(
          (rateDetail) => rateDetail.rateDataID === rateDataID,
        )?.lockEffectiveDate || null;
      const wcpPolicyApplied = row.original.wcpPolicyApplied;
      return (
        <StyledBestExRowSelected data-isSelected={isSelectedRow}>
          <Grid width="100%" justifyContent="flex-end" alignItems="center">
            <StyledBestExProgramLinks
              onClick={() => {
                onClickPrice(rowData);
              }}
            >
              details
            </StyledBestExProgramLinks>
          </Grid>
        </StyledBestExRowSelected>
      );
    },
  },
];
